import { L } from '../../../utils/logger';

// Decoded AudioBuffer cache (LRU + in-flight)
type CacheEntry = { buffer: AudioBuffer; bytes: number; lastUsed: number };
type AudioBufferCache = {
  map: Map<string, CacheEntry>;
  inflight: Map<string, Promise<AudioBuffer>>;
  maxBytes: number;
  currentBytes: number;
};

let __audioBufCache__: AudioBufferCache | null = null;

function estimateBufferBytes(buf: AudioBuffer): number {
  return (buf.numberOfChannels || 1) * buf.length * 4; // 32-bit float
}

export function getAudioBufferCache(): AudioBufferCache {
  if (__audioBufCache__) return __audioBufCache__;
  // Kill-switch to disable decoded PCM caching for A/B tests
  try {
    const env = (process as any)?.env?.REACT_APP_MEDIA_DISABLE_DECODED_CACHE;
    const ls = (() => { try { return localStorage.getItem('MEDIA_DISABLE_DECODED_CACHE'); } catch { return null; } })();
    const off = (env != null && (env === '1' || String(env).toLowerCase() === 'true')) || (ls != null && (ls === '1' || String(ls).toLowerCase() === 'true'));
    if (off) {
      __audioBufCache__ = { map: new Map(), inflight: new Map(), maxBytes: 0, currentBytes: 0 };
      return __audioBufCache__;
    }
  } catch {}
  __audioBufCache__ = {
    map: new Map(),
    inflight: new Map(),
    // Default tighter cap; override via env/localStorage (MB)
    maxBytes: (() => {
      try { const v = (process as any)?.env?.REACT_APP_MEDIA_DECODED_CACHE_MB; if (v != null) return Math.max(32, (parseInt(String(v), 10) || 256)) * 1024 * 1024; } catch {}
      try { const v2 = localStorage.getItem('MEDIA_DECODED_CACHE_MB'); if (v2 != null) return Math.max(32, (parseInt(String(v2), 10) || 256)) * 1024 * 1024; } catch {}
      return 256 * 1024 * 1024; // 256 MB
    })(),
    currentBytes: 0,
  };
  return __audioBufCache__;
}

function enforceAudioBudget() {
  const cache = getAudioBufferCache();
  while (cache.currentBytes > cache.maxBytes && cache.map.size > 0) {
    let lruKey: string | null = null; let lruAt = Infinity;
    cache.map.forEach((v, k) => { if (v.lastUsed < lruAt) { lruAt = v.lastUsed; lruKey = k; } });
    if (lruKey) {
      const ev = cache.map.get(lruKey)!; cache.map.delete(lruKey);
      cache.currentBytes = Math.max(0, cache.currentBytes - ev.bytes);
    } else { break; }
  }
}

export async function getOrLoadBuffer(
  ctx: AudioContext,
  key: string,
  loader: () => Promise<AudioBuffer>,
): Promise<AudioBuffer> {
  const cache = getAudioBufferCache();
  const now = Date.now();
  const hit = cache.map.get(key);
  if (hit) { hit.lastUsed = now; return hit.buffer; }
  const inflight = cache.inflight.get(key);
  if (inflight) return inflight;
  const p = (async () => {
    try {
      const buf = await loader();
      const bytes = estimateBufferBytes(buf);
      // Pre-evict until there is room, then insert and enforce again
      while (cache.currentBytes + bytes > cache.maxBytes && cache.map.size > 0) {
        let lruKey: string | null = null; let lruAt = Infinity;
        cache.map.forEach((v, k) => { if (v.lastUsed < lruAt) { lruAt = v.lastUsed; lruKey = k; } });
        if (lruKey) {
          const ev = cache.map.get(lruKey)!; cache.map.delete(lruKey);
          cache.currentBytes = Math.max(0, cache.currentBytes - ev.bytes);
        } else { break; }
      }
      cache.map.set(key, { buffer: buf, bytes, lastUsed: now });
      cache.currentBytes += bytes;
      enforceAudioBudget();
      return buf;
    } catch (e: any) {
      // If the loader aborted, do not poison inflight with a rejected promise
      if ((e?.name || '').includes('AbortError')) {
        cache.inflight.delete(key);
      }
      throw e;
    } finally {
      cache.inflight.delete(key);
    }
  })();
  cache.inflight.set(key, p);
  return p;
}

// Compressed (ArrayBuffer) cache
type CompressedEntry = { data: ArrayBuffer; bytes: number; lastUsed: number };
type CompressedCache = {
  map: Map<string, CompressedEntry>;
  inflight: Map<string, Promise<ArrayBuffer>>;
  maxBytes: number;
  currentBytes: number;
};

let __compressedCache__: CompressedCache | null = null;

export function getCompressedCache(): CompressedCache {
  if (__compressedCache__) return __compressedCache__;
  __compressedCache__ = {
    map: new Map(),
    inflight: new Map(),
    // Default tighter cap; override via env/localStorage (MB)
    maxBytes: (() => {
      try { const v = (process as any)?.env?.REACT_APP_MEDIA_COMPRESSED_CACHE_MB; if (v != null) return Math.max(16, (parseInt(String(v), 10) || 96)) * 1024 * 1024; } catch {}
      try { const v2 = localStorage.getItem('MEDIA_COMPRESSED_CACHE_MB'); if (v2 != null) return Math.max(16, (parseInt(String(v2), 10) || 96)) * 1024 * 1024; } catch {}
      return 96 * 1024 * 1024; // 96 MB
    })(),
    currentBytes: 0,
  };
  return __compressedCache__;
}

function enforceCompressedBudget() {
  const cache = getCompressedCache();
  while (cache.currentBytes > cache.maxBytes && cache.map.size > 0) {
    let lruKey: string | null = null; let lruAt = Infinity;
    cache.map.forEach((v, k) => { if (v.lastUsed < lruAt) { lruAt = v.lastUsed; lruKey = k; } });
    if (lruKey) {
      const ev = cache.map.get(lruKey)!; cache.map.delete(lruKey);
      cache.currentBytes = Math.max(0, cache.currentBytes - ev.bytes);
    } else { break; }
  }
}

export async function getOrFetchCompressed(url: string, signal?: AbortSignal): Promise<ArrayBuffer> {
  const cache = getCompressedCache();
  const now = Date.now();
  const hit = cache.map.get(url);
  if (hit) { hit.lastUsed = now; return hit.data; }
  const inflight = cache.inflight.get(url);
  if (inflight) return inflight;
  const p = (async () => {
    try {
      // Optional: disable browser HTTP cache to better observe memory behavior
      const noStore = (() => {
        try { const v = (process as any)?.env?.REACT_APP_MEDIA_FETCH_NOSTORE; if (v != null) return v === '1' || String(v).toLowerCase() === 'true'; } catch {}
        try { const w = (window as any); if (w.__MEDIA_FETCH_NOSTORE === true) return true; const v2 = localStorage.getItem('MEDIA_FETCH_NOSTORE'); if (v2 != null) return v2 === '1' || String(v2).toLowerCase() === 'true'; } catch {}
        return false;
      })();
      const resp = await fetch(url, {
        mode: 'cors',
        cache: noStore ? 'no-store' : 'force-cache',
        headers: noStore ? { 'Cache-Control': 'no-store' } as any : undefined,
        signal,
      });
      const ab = await resp.arrayBuffer();
      const bytes = ab.byteLength;
      while (cache.currentBytes + bytes > cache.maxBytes && cache.map.size > 0) {
        let lruKey: string | null = null; let lruAt = Infinity;
        cache.map.forEach((v, k) => { if (v.lastUsed < lruAt) { lruAt = v.lastUsed; lruKey = k; } });
        if (lruKey) {
          const ev = cache.map.get(lruKey)!; cache.map.delete(lruKey);
          cache.currentBytes = Math.max(0, cache.currentBytes - ev.bytes);
        } else { break; }
      }
      cache.map.set(url, { data: ab, bytes, lastUsed: now });
      cache.currentBytes += bytes;
      enforceCompressedBudget();
      return ab;
    } catch (e: any) {
      if ((e?.name || '').includes('AbortError')) {
        cache.inflight.delete(url);
      }
      throw e;
    } finally {
      cache.inflight.delete(url);
    }
  })();
  cache.inflight.set(url, p);
  return p;
}

/** Clear all decoded AudioBuffers from cache and cancel inflight loads. */
export function flushAudioBufferCache() {
  const cache = getAudioBufferCache();
  try { cache.map.clear(); } catch {}
  try { cache.inflight.clear(); } catch {}
  cache.currentBytes = 0;
}

/** Clear all compressed ArrayBuffers from cache and cancel inflight fetches. */
export function flushCompressedCache() {
  const cache = getCompressedCache();
  try { cache.map.clear(); } catch {}
  try { cache.inflight.clear(); } catch {}
  cache.currentBytes = 0;
}

/** Clear both decoded and compressed caches. */
export function flushAllCaches() {
  try { flushAudioBufferCache(); } catch {}
  try { flushCompressedCache(); } catch {}
}

/** Enforce budgets across both caches (LRU-evict until under caps). */
export function enforceAllCacheBudgets() {
  try { enforceAudioBudget(); } catch {}
  try { enforceCompressedBudget(); } catch {}
}

/** On swap, either flush caches if not resuming, or enforce budgets to prevent drift. */
export function onSwapCacheMaintenance(aboutToResume: boolean) {
  if (!aboutToResume) {
    try { flushAllCaches(); } catch {}
  } else {
    try { enforceAllCacheBudgets(); } catch {}
  }
}

/** Lightweight telemetry to log cache states for triage. */
export function logDecodedCacheStats(reason: string) {
  try {
    const c = getAudioBufferCache();
    L.info('media:scope', 'decoded-cache', {
      reason,
      currentBytes: c.currentBytes,
      maxBytes: c.maxBytes,
      entries: c.map.size,
    });
  } catch {}
}

export function logCompressedCacheStats(reason: string) {
  try {
    const c = getCompressedCache();
    L.info('media:scope', 'compressed-cache', {
      reason,
      currentBytes: c.currentBytes,
      maxBytes: c.maxBytes,
      entries: c.map.size,
    });
  } catch {}
}
